import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@utils/constants';
import { formatDurationRange, calculateEstimatedPickupTime } from '@utils/formatters';
import { useToastContext } from '../../context/ToastContext';
import { listPricingConfigs, type PricingConfig, type PricingTier } from '../../api/pricing';
import { searchPaymentPlans, previewSubscription, type PaymentPlanV3, type SubscriptionPreview } from '../../api/payment';
import { AppHeader } from '@components/layout/AppHeader';
import { InfoCard } from '@/components/ui/InfoCard';
import { PlanCard } from '@/components/ui/PlanCard';
import { EnvironmentOutlined, BulbOutlined, CloseOutlined, CalendarOutlined } from '@ant-design/icons';
import { formatDate } from '../../utils/dateFormatter';
import './styles/selectplans.css';
import '@styles/loader.css';

interface SelectPlansState {
  studentId: string;
  student: any;
  distance?: number;
  distanceText?: string;
  duration?: number;
  durationText?: string;
  startDate?: string;
  estimatedPickupTime?: string;
}

interface PricingOption {
  id: string;
  config: PricingConfig;
  tier: PricingTier;
  capacity: string;
  capacityLabel: string;
}

export const SelectPlans: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SelectPlansState;
  const { addToast } = useToastContext();
  const planCardRefs = useRef(new Map());

  const [allPricingConfigs, setAllPricingConfigs] = useState<PricingConfig[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<PricingOption[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState<string>('');

  // Payment plans state
  const [paymentPlans, setPaymentPlans] = useState<PaymentPlanV3[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [showWarningCard, setShowWarningCard] = useState(true);
  const [previews, setPreviews] = useState<Record<string, SubscriptionPreview>>({});
  const [bestValuePlanId, setBestValuePlanId] = useState<string | null>(null);

  const [loading] = useState(false);
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  const distance = state?.distance || 0;
  const duration = state?.duration || 0;
  const estimatedPickupTimeFromState = state?.estimatedPickupTime;
  const [estimatedPickupTime, setEstimatedPickupTime] = useState<string | null>(null);

  // Get startDate from localStorage or state using useMemo
  const startDate = useMemo(() => {
    try {
      const pending = JSON.parse(localStorage.getItem('pendingSubscription') || '{}');
      if (pending.startDate) return pending.startDate;
    } catch (e) {
      console.error('Error reading from localStorage:', e);
    }
    return state?.startDate || new Date().toISOString().slice(0, 10);
  }, [state?.startDate]);

  // Validation on mount
  useEffect(() => {
    if (!state?.studentId || !state?.student) {
      navigate(ROUTES.WELCOME);
      return;
    }

    if (!distance || distance <= 0) {
      addToast({
        type: 'error',
        title: 'Distance Required',
        message: 'Distance calculation is required to show pricing. Please go back.',
      });
    }
  }, [state, navigate, distance]);

  // Calculate estimated pickup time when duration and shift start time are available
  useEffect(() => {
    // First, check if we have estimated pickup time from state (passed from ReviewDetails)
    if (estimatedPickupTimeFromState) {
      setEstimatedPickupTime(estimatedPickupTimeFromState);
      return;
    }

    // Otherwise, calculate it locally if we have the required data
    // We need to get shift start time from the preview's shiftDetails
    if (duration && previews && Object.keys(previews).length > 0) {
      const firstPreview = previews[Object.keys(previews)[0]];
      if ((firstPreview?.shiftDetails as any)?.schedule?.startsAt) {
        const pickupTime = calculateEstimatedPickupTime(
          (firstPreview.shiftDetails as any).schedule.startsAt,
          duration
        );
        setEstimatedPickupTime(pickupTime);
      }
    }
  }, [duration, estimatedPickupTimeFromState, previews]);

  // Fetch all pricing configs for student's shifts
  useEffect(() => {
    if (state?.student?.shifts && state?.student?.shifts.length > 0 && distance > 0) {
      fetchAllPricingConfigs();
    }
  }, [state?.student?.shifts, distance]);

  const fetchAllPricingConfigs = async () => {
    try {
      setLoadingConfigs(true);

      const allConfigs: PricingConfig[] = [];

      // Fetch configs for all shifts
      for (const shiftId of state.student.shifts) {
        const configs = await listPricingConfigs(shiftId);
        allConfigs.push(...configs);
      }

      setAllPricingConfigs(allConfigs);

      // Set default values - auto-select first option
      if (allConfigs.length > 0) {
        // Apply default filters and get options
        const filtered = filterPricingOptions(allConfigs, 'TWO_WAY', 'DOOR_STEP', state.student.tags?.transportType || '');
        setFilteredOptions(filtered);

        if (filtered.length > 0) {
          setSelectedOptionId(filtered[0].id);
        }
      }
    } catch (err: any) {
      console.error('[SelectPlans] Error fetching pricing configs:', err);
      addToast({
        type: 'error',
        title: 'Failed to Load Pricing',
        message: 'Unable to load pricing configurations. Please try again.',
      });
    } finally {
      setLoadingConfigs(false);
    }
  };

  const filterPricingOptions = (
    configs: PricingConfig[],
    packageType: string,
    pickupType: string,
    vehicleType: string
  ): PricingOption[] => {
    const options: PricingOption[] = [];

    configs.forEach((config) => {
      // Filter by selected filters
      if (packageType && config.packageType !== packageType) return;
      if (pickupType && config.pickupType !== pickupType) return;
      if (vehicleType && config.vehicleType !== vehicleType) return;

      // Extract matching tiers by distance
      const pricingByCapacity = config.pricingByCapacity || {};

      Object.entries(pricingByCapacity).forEach(([capacity, tiers], index) => {
        // Find tier that matches distance
        const matchingTier = tiers.find(
          (tier) =>
            tier.rangeKm?.from != null &&
            tier.rangeKm?.to != null &&
            distance >= tier.rangeKm.from &&
            distance < tier.rangeKm.to
        );

        if (matchingTier) {
          const capacityLabel = capacity === '0' ? 'Shared' : `${capacity} Seater`;
          options.push({
            id: `${config.id}-${capacity}-${index}`,
            config,
            tier: matchingTier,
            capacity,
            capacityLabel,
          });
        }
      });
    });

    return options;
  };

  // Fetch payment plans when selected pricing option changes
  useEffect(() => {
    const selectedOption = filteredOptions.find((opt) => opt.id === selectedOptionId);
    if (selectedOption?.config?.id) {
      fetchPaymentPlans(selectedOption.config.id);
    }
  }, [selectedOptionId, filteredOptions]);

  const fetchPaymentPlans = async (pricingConfigId: string) => {
    try {
      setLoadingPlans(true);
      const plans = await searchPaymentPlans(pricingConfigId);

      setPaymentPlans(plans);

      // Reset previews
      setPreviews({});

      // Fetch previews for all plans in parallel
      const previewResults = await Promise.all(
        plans.map(plan => 
          previewSubscription({
            granteeId: state.studentId,
            paymentPlanId: plan.id,
            startDate,
          }).then(preview => ({ planId: plan.id, preview }))
          .catch(err => {
            console.error('[SelectPlans] Error fetching preview for plan:', plan.id, err);
            return null;
          })
        )
      );

      // Update previews state with successful results
      const newPreviews: Record<string, SubscriptionPreview> = {};
      previewResults.forEach(result => {
        if (result) {
          newPreviews[result.planId] = result.preview;
        }
      });
      setPreviews(newPreviews);

      // Determine best value plan (highest discount) and auto-select it
      let computedBestId: string | null = null;
      let maxDiscount = -1;
      plans.forEach((plan) => {
        const preview = newPreviews[plan.id];
        if (preview) {
          const discount = (preview.paymentPlan?.discount || 0) * 100;
          if (discount > maxDiscount) {
            maxDiscount = discount;
            computedBestId = plan.id;
          }
        }
      });
      setBestValuePlanId(computedBestId);
      setSelectedPlanId(computedBestId || plans[0]?.id || '');
    } catch (err: any) {
      console.error("[SelectPlans] Error fetching payment plans:", err);
      addToast({
        type: "error",
        title: "Failed to Load Payment Plans",
        message: "Unable to load payment plans. Please try again.",
      });
    } finally {
      setLoadingPlans(false);
    }
  };

  if (!state?.student) return null;

  // Derive distance & duration from state if available
  const distanceKm = state.distance ? `${state.distance.toFixed(2)} km` : "";
  const distanceDisplay = state.distanceText || distanceKm;
  const travelTimeDisplay = formatDurationRange(duration);

  const handleContinue = () => {
    if (!selectedOptionId) {
      addToast({
        type: 'error',
        title: 'Selection Required',
        message: 'Please select a pricing option to continue.',
      });
      return;
    }

    const selectedOption = filteredOptions.find((opt) => opt.id === selectedOptionId);
    if (!selectedOption) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'Selected option not found. Please try again.',
      });
      return;
    }

    if (!selectedPlanId) {
      addToast({
        type: 'error',
        title: 'Plan Selection Required',
        message: 'Please select a payment plan to continue.',
      });
      return;
    }

    const selectedPlan = paymentPlans.find(p => p.id === selectedPlanId);
    const selectedPreview = previews[selectedPlanId];

    if (!selectedPlan || !selectedPreview) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'Selected plan or preview not found. Please try again.',
      });
      return;
    }

    // Get the amount to pay from the ref
    const planCardRef = planCardRefs.current.get(selectedPlanId);
    const calculatedValues = planCardRef?.getCalculatedValues();
    const amountToPay = calculatedValues?.payNowAmount || 0;
    
    // Store the subscription payload in localStorage
    const subscriptionPayload = {
      granteeId: state.studentId,
      paymentPlanId: selectedPlanId,
      startDate,
      plan: selectedPlan,
      preview: selectedPreview,
      amountToPay,
    };

    localStorage.setItem('pendingSubscription', JSON.stringify(subscriptionPayload));

    // Navigate to UserDetails with the same payload as ChoosePlan
    navigate(ROUTES.USER_DETAILS, {
      state: {
        studentId: state.studentId,
        student: state.student,
        selectedPricing: {
          config: selectedOption.config,
          tier: selectedOption.tier,
          capacity: selectedOption.capacity,
          capacityLabel: selectedOption.capacityLabel,
        },
        distance: state.distance,
        duration: state.duration,
        selectedPlan,
        subscriptionPreview: selectedPreview,
        startDate,
        amountToPay,
        estimatedPickupTime: estimatedPickupTime || null,
      },
    });
  };

  // Sort plans: best value first, then one-time payment, then rest
  const getSortedPlans = (): PaymentPlanV3[] => {
    if (paymentPlans.length === 0) return paymentPlans;

    const sorted: PaymentPlanV3[] = [];
    const remaining: PaymentPlanV3[] = [];

    paymentPlans.forEach((plan) => {
      if (plan.id === bestValuePlanId) {
        sorted.push(plan);
      } else if (plan.planType === 'ONE_TIME') {
        remaining.unshift(plan); // Add to front of remaining
      } else {
        remaining.push(plan);
      }
    });

    return [...sorted, ...remaining];
  };

  return (
    <div className="select-plans-page">
      <AppHeader />

      <div className="select-plans-page__container">
        <div className="select-plans-page__content">
          {/* Title Section */}
          <div className="select-plans-page__title-section">
            <h1 className="select-plans-page__title">Choose a transport plan</h1>
            <p className="select-plans-page__subtitle">
              Plans customized based on your campus and distance
            </p>
          </div>

          {/* Distance Info Card */}
          <div className="select-plans-page__distance-info">
            <EnvironmentOutlined style={{ fontSize: 20 }} className="select-plans-page__distance-icon" />
            <div className="select-plans-page__distance-content">
              <p className="select-plans-page__distance-label">
                Estimated Distance: {distanceDisplay}
              </p>
              {estimatedPickupTime && (
                <p className="select-plans-page__distance-time">
                  Estimated pickup time: <strong>{estimatedPickupTime}</strong>
                </p>
              )}
              {travelTimeDisplay && (
                <p className="select-plans-page__distance-time">
                  Travel time: <strong>{travelTimeDisplay}</strong>
                </p>
              )}
            </div>
          </div>

          {/* Service Dates Card */}
          {paymentPlans.length > 0 && previews[paymentPlans[0].id] && (
            <div className="select-plans-page__service-dates-card">
              <div className="select-plans-page__service-dates-icon">
                <CalendarOutlined style={{ fontSize: 18 }} />
              </div>
              <div className="select-plans-page__service-dates-content">
                <p className="select-plans-page__service-start">
                  Subscription start date: {startDate ? formatDate(startDate) : formatDate(previews[paymentPlans[0].id].shiftDetails?.validFrom ?? '')}
                </p>
                <p className="select-plans-page__service-end">
                  Subscription end date: {formatDate(previews[paymentPlans[0].id].shiftDetails?.validUntil ?? '')}
                </p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {(loadingConfigs || loadingPlans) && (
            <div className="select-plans-page__loading-plans">
              <span className="btn-loader btn-loader--dark"></span>
              <span>Loading plans...</span>
            </div>
          )}

          {/* Payment Plans */}
          {!loadingConfigs && !loadingPlans && paymentPlans.length > 0 && (
            <div className="select-plans-page__plans">
              {getSortedPlans().map((plan, index) => {
                const isSelected = selectedPlanId === plan.id;
                const isBestValue = plan.id === bestValuePlanId;

                return (
                  <div key={plan.id} className="select-plans-page__plan-wrapper">
                    {previews[plan.id] && (
                      <PlanCard
                        ref={(ref) => {
                          if (ref) planCardRefs.current.set(plan.id, ref);
                        }}
                        id={plan.id}
                        isSelected={isSelected}
                        onSelect={() => setSelectedPlanId(plan.id)}
                        colorClass={`plan-color-${(index % 3) + 1}`}
                        isBestValue={isBestValue}
                        subscriptionPreview={previews[plan.id]}
                        paymentPlan={plan}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* No Plans Available */}
          {!loadingConfigs && !loadingPlans && paymentPlans.length === 0 && allPricingConfigs.length > 0 && (
            <div className="select-plans-page__no-plans">
              <p>No payment plans available for this selection.</p>
              <p>Please contact support for assistance.</p>
            </div>
          )}

          {!loadingConfigs && !loadingPlans && allPricingConfigs.length === 0 && (
            <div className="select-plans-page__no-plans">
              <p>No pricing configurations available.</p>
              <p>Please contact support for assistance.</p>
            </div>
          )}

          {/* Warning Info Card — "Why cheaper options look cheaper" */}
          {showWarningCard && paymentPlans.length > 0 && (
            <div className="select-plans-page__warning-wrapper">
              <InfoCard
                icon={<BulbOutlined style={{ fontSize: 20, color: "#e65100" }} />}
                title="Why cheaper options look cheaper"
                description="Overloaded vehicles, non-compliant seating, no verified routing. Zero Moblt follows safety-first pricing."
                className="select-plans-page__warning-card"
                cardStyle={{
                  backgroundColor: "#fff8f0",
                  border: "1.5px solid #ffd5b0",
                  borderRadius: 14,
                  position: "relative",
                  marginBottom: 24,
                }}
                iconStyle={{ color: "#e65100" }}
                titleStyle={{ color: "#e65100", fontSize: 13, fontWeight: 700, marginBottom: 2 }}
                descriptionStyle={{ color: "#a04000", fontSize: 12, lineHeight: 1.5 }}
              />
              <button
                className="select-plans-page__warning-close"
                onClick={() => setShowWarningCard(false)}
                aria-label="Dismiss"
              >
                <CloseOutlined style={{ fontSize: 12 }} />
              </button>
            </div>
          )}

          {/* Footer Button */}
          <div className="select-plans-page__footer">
            <button
              className={`select-plans-page__continue-btn ${loading ? 'btn-loading' : ''} ${selectedPlanId ? 'active' : ''}`}
              onClick={handleContinue}
              disabled={!selectedPlanId || loading || loadingConfigs || loadingPlans || paymentPlans.length === 0}
            >
              {loading ? (
                <>
                  <span className="btn-loader btn-loader--dark"></span>
                  Processing...
                </>
              ) : (
                'Continue with this plan'
              )}
            </button>
            {selectedPlanId && (
              <p className="select-plans-page__footer-note">
                You're only paying the token amount today
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectPlans;

